import React from 'react';
import { connect } from 'react-redux';
import { actionCreators } from 'react/api/actions/crud';
import {
  Field, initialize, change, untouch
} from 'redux-form';
import PropTypes from 'prop-types';
import CrudContext from 'react/contexts/DefaultContext';

class BaseField extends React.Component {
  constructor(props, context) {
    super(props);
    this.fieldContentRef = React.createRef();

    const { addFieldCriteriaAction, fieldCriteria, name } = props;
    const { entity, isEdit } = context;

    if (isEdit && !name.includes('[')) {
      addFieldCriteriaAction({ entity, fieldCriteria: fieldCriteria || [name] });
    }
  }

  componentDidMount() {
    const {
      name,
      gridFormatter,
      defaultValue,
      initializeAction,
      doNotShowOnGrid
    } = this.props;
    const { entity, isGrid } = this.context;

    if (isGrid) {
      if (!doNotShowOnGrid) {
        App.Forms[entity].fields[name] = (gridFormatter || this.gridFormatter)(this.props);
      }
    } else if (defaultValue !== null) {
      initializeAction(entity, { [name]: defaultValue }, { keepValues: true });
    }
  }

  componentDidUpdate(prevProps) {
    const { isGrid, entity } = this.context;
    const {
      monetField, shouldShow, name, changeAction, untouchAction, value
    } = this.props;

    if (!isGrid) {
      const ref = this.fieldContentRef.current;
      if (monetField && ref && ref.childElementCount === 0) {
        ref.appendChild(monetField.rootEl);
      }
      if (!shouldShow && prevProps.shouldShow) {
        changeAction(entity, name, null);
        untouchAction(entity, name);
      }
      if (value !== undefined) {
        changeAction(entity, name, value);
      }
    }
  }

  gridFormatter = () => {
    const { label, name } = this.props;
    return {
      id: name,
      name,
      label,
      type: 'TEXT'
    };
  };

  renderFieldHint = ({ active }) => {
    const { hint, focusHint } = this.props;
    return (hint || (focusHint && active)) ? (
      <div className="fieldHint">
        {Monet.I18n.getResourcesByKey(focusHint || hint)}
      </div>
    ) : null;
  };

  renderCustomFieldHint = ({ active }) => {
    const { customHint } = this.props;
    return (customHint || active) ? (
      <div className="fieldHint">
        {customHint}
      </div>
    ) : null;
  };

  renderFieldMessage = (label, touched, error, warning) => (
    <div className="fieldMessage">
      { touched && ((error || warning) && (
        <span>
          { Monet.I18n.getResourcesByKey(error || warning, Monet.I18n.getResourcesByKey(label)) }
        </span>
      ))}
    </div>
  );

  renderFieldWrapper = ({
    input,
    label,
    required,
    renderFieldContent,
    entity,
    meta: {
      touched, error, warning, active
    },
    fieldClassName,
    disabled,
    disabledOnEdit,
    shouldShow,
    noFieldMessage,
    ...remainingProps
  }) => (shouldShow ? (
    <div className={`field ${fieldClassName || ''} ${touched && error ? 'error' : ''}`}>
      <label htmlFor={`${input.name}-${entity}`}>
        {`${label ? Monet.I18n.getResourcesByKey(label) : ''}${required ? '*' : ''}`}
      </label>
      <div>
        <CrudContext.Consumer>
          {({ isEdit }) => (
            renderFieldContent({
              input,
              entity,
              id: `${input.name}-${entity}`,
              ref: this.fieldContentRef,
              disabled: (isEdit && disabledOnEdit) || disabled,
              ...remainingProps
            })
          )}
        </CrudContext.Consumer>
        { this.renderFieldHint({ active }) }
        { this.renderCustomFieldHint({ active }) }
        { !noFieldMessage ? this.renderFieldMessage(label, touched, error, warning) : null }
      </div>
    </div>
  ) : null);

  render() {
    const {
      required,
      customValidation,
      shouldShow,
      ...remainingProps
    } = this.props;

    const { entity, isGrid } = this.context;

    if (isGrid) {
      return null;
    }

    const validate = (value, allValues) => {
      if (customValidation && shouldShow) {
        return customValidation(value, allValues, remainingProps);
      }
      if (required && !value && shouldShow) {
        return 'general.mandatoryField';
      }
      return undefined;
    };

    return (
      <Field
        entity={entity}
        component={this.renderFieldWrapper}
        validate={validate}
        required={required}
        shouldShow={shouldShow}
        {...remainingProps}
      />
    );
  }
}

BaseField.contextType = CrudContext;

BaseField.propTypes = {
  required: PropTypes.bool,
  customValidation: PropTypes.func,
  monetField: PropTypes.object,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  gridFormatter: PropTypes.func,
  hint: PropTypes.string,
  focusHint: PropTypes.string,
  defaultValue: PropTypes.any,
  initializeAction: PropTypes.func.isRequired,
  changeAction: PropTypes.func.isRequired,
  untouchAction: PropTypes.func.isRequired,
  addFieldCriteriaAction: PropTypes.func.isRequired,
  shouldShow: PropTypes.bool,
  value: PropTypes.any,
  fieldCriteria: PropTypes.array,
  doNotShowOnGrid: PropTypes.bool
};

BaseField.defaultProps = {
  required: false,
  customValidation: null,
  monetField: null,
  gridFormatter: null,
  hint: null,
  focusHint: null,
  defaultValue: null,
  shouldShow: true,
  value: undefined,
  fieldCriteria: null,
  doNotShowOnGrid: false
};

const mapDispatchToProps = {
  initializeAction: initialize,
  changeAction: change,
  untouchAction: untouch,
  addFieldCriteriaAction: actionCreators.addFieldCriteria
};

export default connect(null, mapDispatchToProps)(BaseField);
